import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import 'moment/locale/pt-br';

import EstateUnitFilter from '../../components/EstateUnitFilter';
import FilterBar, { FilterField, FilterValues } from '../../components/Filter';
import { CustoDropDownItemProps } from '../../components/types';
import { BondInsuranceForGetBondInsurancesResponseDTO } from '../../dtos/bond-insurance-list/get-bond-insurances-response.dto';
import { GetExpiredBondInsurancesResponseDTO } from '../../dtos/expired-bond-insurance/get-expired-bond-insurances-response.dto';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import {
  getBondInsurances,
  sortBondInsurances
} from '../../services/bond-insurance';
import { dangerNotification } from '../../services/notification';
import { iRootState } from '../../store';
import { AvailableBrokersAndEstatesState } from '../../store/types/temp-types';
import { appendEstateUnitIdToFilter } from '../../utils/estateUnitFilter.util';
import { isBackOfficeUser } from '../../utils/isBackOfficeUser';
import { isEstateRole } from '../../utils/isEstateRole';
import ExpiredBondInsurancesTable from './ExpiredBondInsurancesTable';
import { FilterData, ListingRouteInfo } from './types';

export type BondInsuranceListProps = {
  availableBrokersAndEstates: iRootState['availableBrokersAndEstates'];
  user: iRootState['user'];
  updateAvailableBrokersAndEstates: (
    availableBrokers: AvailableBrokersAndEstatesState
  ) => AvailableBrokersAndEstatesState | void;
} & RouteComponentProps<ListingRouteInfo>;

interface LocationState {
  bondInsuranceId?: string;
}

type SearchField = 'name' | 'document' | 'address';

type ApiFilters = FilterData & {
  estateUnitId?: string;
  action?: string;
};

const SEARCH_FIELDS: SearchField[] = ['name', 'document', 'address'];

const SEARCH_TYPE_OPTIONS = [
  { value: 'document', text: 'CPF/CNPJ' },
  { value: 'name', text: 'Nome' },
  { value: 'address', text: 'Endereço' }
];

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    return moment(value).format('YYYY-MM-DD');
  }
  const asString = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(asString)) {
    return asString.substring(0, 10);
  }
  const parsed = moment(asString, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : asString;
};

const buildInitialFilterValues = (singleEstateId: string): FilterValues => ({
  type: 'document',
  argument: '',
  estate_id: singleEstateId,
  start: '',
  end: '',
  estateUnitId: ''
});

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const searchType = values.type as SearchField | undefined;
  let argument = String(values.argument || '').trim();

  if (searchType === 'document') {
    argument = argument.replace(/\D/g, '');
  }

  const filter: ApiFilters = {
    isRv: true,
    status: 'EXPIRED'
  };

  SEARCH_FIELDS.forEach(field => {
    filter[field] = undefined;
  });

  if (searchType && argument && SEARCH_FIELDS.includes(searchType)) {
    filter[searchType] = argument;
  }

  if (values.estate_id) filter.estate_id = String(values.estate_id);

  const start = formatDateValue(values.start);
  const end = formatDateValue(values.end);
  if (start) filter.start = start;
  if (end) filter.end = end;

  return appendEstateUnitIdToFilter(
    filter as Record<string, unknown>,
    String(values.estateUnitId || '')
  ) as ApiFilters;
};

const ExpiredBondInsurances = ({
  availableBrokersAndEstates,
  user
}: BondInsuranceListProps) => {
  const location = useLocation();
  const locationState = location.state as LocationState | undefined;

  const brokers: CustoDropDownItemProps[] = availableBrokersAndEstates.brokers;
  const estates: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;

  const isBackOffice = isBackOfficeUser();
  const isEstateUser = isEstateRole(user?.role);

  const singleEstateId = useMemo(() => {
    if (estates.length !== 1) return '';
    const value = estates[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [estates]);

  const singleBrokerId = useMemo(() => {
    if (brokers.length !== 1) return '';
    const value = brokers[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [brokers]);

  const initialFilterValues = useMemo(
    () => buildInitialFilterValues(singleEstateId),
    [singleEstateId]
  );

  const hasDeepLink = Boolean(locationState?.bondInsuranceId);

  const [bondInsurances, setBondInsurances] = useState<
    BondInsuranceForGetBondInsurancesResponseDTO[]
  >([]);
  const [expiredBondInsurances] = useState<GetExpiredBondInsurancesResponseDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ascendingOrder] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const [bondInsuranceId, setBondInsuranceId] = useState<string | undefined>(
    locationState?.bondInsuranceId
  );
  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const [isEnabledToFetch, setIsEnabledToFetch] = useState(
    hasDeepLink || !isBackOffice
  );

  const appliedFiltersRef = useRef(appliedFilters);
  const bondInsuranceIdRef = useRef(bondInsuranceId);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    bondInsuranceIdRef.current = bondInsuranceId;
  }, [bondInsuranceId]);

  const estateOptions = useMemo(
    () =>
      estates.map(item => {
        const valueId =
          typeof item.value === 'object' && item.value?.id
            ? item.value.id
            : item.value;
        return {
          value: String(valueId || ''),
          text: String(item.text || '')
        };
      }),
    [estates]
  );

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'document',
        types: SEARCH_TYPE_OPTIONS.map(option => ({
          value: option.value,
          label: option.text
        })),
        width: 1.6
      }
    ];

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'estate_id',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(estateOptions),
        getDisplayValue: values => {
          const option = estateOptions.find(
            item => String(item.value) === String(values.estate_id)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push(
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'custom',
        name: 'estateUnitId',
        label: 'Unidade',
        getDisplayValue: values => {
          if (!values.estateUnitId) return undefined;
          if (values.estateUnitId === 'null') return 'Sem unidade';
          return String(values.estateUnitId);
        },
        render: ({ values, setValue }) => (
          <EstateUnitFilter
            estateId={String(values.estate_id || user?.estate?.id || '')}
            value={values.estateUnitId || ''}
            onChange={value => setValue('estateUnitId', value)}
            upward
          />
        )
      }
    );

    return fields;
  }, [isEstateUser, estateOptions, user?.estate?.id]);

  const resolveBrokerId = useCallback((): string | undefined => {
    if (
      user.role === UserRolesEnum.brokerAnalyst ||
      user.role === UserRolesEnum.brokerAdmin ||
      user.role === UserRolesEnum.accountManager
    ) {
      return user?.broker?.id ? String(user.broker.id) : undefined;
    }

    if (singleBrokerId) return singleBrokerId;
    if (user?.broker?.id) return String(user.broker.id);
    return undefined;
  }, [singleBrokerId, user?.broker?.id, user.role]);

  const getData = useCallback(async () => {
    setIsLoading(true);

    try {
      const filter: ApiFilters = {
        ...appliedFiltersRef.current,
        isRv: true,
        status: 'EXPIRED',
        ...(bondInsuranceIdRef.current
          ? { bondInsuranceId: bondInsuranceIdRef.current }
          : {})
      };

      if (!filter.estate_id && singleEstateId) {
        filter.estate_id = singleEstateId;
      }

      const brokerId = resolveBrokerId();
      if (brokerId) {
        filter.broker_id = brokerId;
      }

      if (!filter.broker_id) {
        setBondInsurances([]);
        return;
      }

      const response = await getBondInsurances(currentPage, filter);
      const data = sortBondInsurances(response.data, ascendingOrder);

      if (currentPage === 1) {
        const total = await getBondInsurances(currentPage, {
          ...filter,
          action: 'COUNT'
        });
        setDataCount(total.count || total.current || 0);
      }

      setBondInsurances(data);

      if (bondInsuranceIdRef.current) {
        setBondInsuranceId(undefined);
      }
    } catch (e) {
      console.error({ e });
      dangerNotification('Oops...', 'Não foi possível buscar as análises');
    } finally {
      setIsLoading(false);
    }
  }, [ascendingOrder, currentPage, resolveBrokerId, singleEstateId]);

  useEffect(() => {
    window.history.replaceState(
      {
        ...(location.state as any),
        bondInsuranceId: undefined
      },
      ''
    );
  }, [location]);

  useEffect(() => {
    if (!isBackOffice) {
      setIsEnabledToFetch(true);
    }
  }, [isBackOffice]);

  useEffect(() => {
    if (!isEnabledToFetch) return;
    getData();
  }, [currentPage, appliedFilters, isEnabledToFetch, getData]);

  const handleFilterSearch = (values: FilterValues) => {
    const next = normalizeToApiFilter(values);
    if (!next.estate_id && singleEstateId) {
      next.estate_id = singleEstateId;
    }
    setBondInsuranceId(undefined);
    setAppliedFilters(next);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const handleFilterClear = () => {
    setBondInsuranceId(undefined);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  return (
    <>
      <FilterBar
        fields={filterFields}
        initialValues={initialFilterValues}
        onSearch={handleFilterSearch}
        onClear={handleFilterClear}
        loading={isLoading}
        syncToUrl
        urlNamespace="expired"
        hasExternalActiveFilters={Boolean(bondInsuranceId)}
      />

      <ExpiredBondInsurancesTable
        bondInsurances={bondInsurances}
        expiredBondInsurances={expiredBondInsurances}
        isLoading={isLoading}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        getData={() => getData()}
        dataCount={dataCount}
      />
    </>
  );
};

export default ExpiredBondInsurances;
